import React, { useEffect, useState } from 'react'
import entete from "../../../assets/img/entete_pool.png"
import PropTypes from 'prop-types';
import { DocumentFooter } from '../../../components/docs/DocumentFooter';
import { getCompagnieLogo } from '../../../hooks/useFilesHooks';

const frDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

/**
 * MANDAT D'EXPERTISE MÉCANIQUE — note à l'expert lorsque l'expertise est de
 * type MECANIQUE (la note « Docteur / certificats médicaux » de NoteExpert ne
 * convient qu'au médical). Modèle validé :
 *
 *   « Nous soussignés {société}, mandatons par la présente le Cabinet
 *   d'expertise {expert}, à l'effet de procéder à l'expertise mécanique de la
 *   voiture immatriculée {immat} endommagée au cours d'un accident survenu
 *   le {date} à {lieu}. En foi de quoi, nous lui délivrons le présent mandat
 *   d'expertise pour servir et valoir ce que de droit. »
 */
const MandatExpertiseMecanique = React.forwardRef((props, ref) => {
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    // En-tête + pied de page OPTIONNELS : désactivés, le mandat s'imprime « nu », pour être
    // sorti sur le papier à en-tête de l'utilisateur. Par défaut : affichés (comportement
    // actuel inchangé).
    const afficherEnteteEtPied = props?.afficherEnteteEtPied !== false;

    useEffect(() => {
        if (props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL") {
            getCompagnieLogo(props?.sinistre?.contrat?.organisme?.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    var blob = new Blob([imageBytes], { type: "image/png" });
                    setLogoPreviewUrl(URL.createObjectURL(blob));
                })
                .catch((error) => { console.log(error); });
        }
    }, []);

    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    const sinistre = props?.sinistre;
    const expert = props?.expertise?.expert;
    const nomExpert = `${expert?.nom || ''} ${expert?.prenom || ''}`.trim();
    const societe = sinistre?.contrat?.organisme?.raisonSociale || 'Pool TPVM-VT GIE';
    const numSinistre = sinistre?.numSinistre || sinistre?.numeroSinistreM || '';
    const dateSinistre = frDate(sinistre?.dateSinistre);
    // L'expertise MÉCANIQUE porte sur le véhicule de la VICTIME / ADVERSAIRE associé à l'expertise
    // (beneficiaire = Victime, qui porte son propre engin) — PAS sur l'engin assuré. Repli sur l'engin
    // du sinistre puis le 1er engin du contrat si la victime n'a pas d'engin (cas non-adversaire).
    const immatriculation = props?.expertise?.beneficiaire?.engin?.immatriculation
        || sinistre?.engin?.immatriculation
        || sinistre?.contrat?.engins?.[0]?.immatriculation
        || '……………';
    const lieu = sinistre?.lieu || 'Lomé';

    return (
        // Flex colonne + hauteur minimale d'une page A4 (marges d'impression 20mm
        // déduites) : le corps s'étale et le pied de page est poussé EN BAS de la
        // feuille → le mandat occupe toute la page.
        <div ref={ref} style={{
            fontFamily: "Calibri, 'Calibri Light', sans-serif",
            maxWidth: "800px",
            margin: "0 auto",
            padding: "20px 70px",
            lineHeight: 1.6,
            fontSize: "12pt",
            color: "#000000",
            display: "flex",
            flexDirection: "column",
            minHeight: "250mm",
            boxSizing: "border-box"
        }} className="mandat-mecanique">
            {/* ⚠️ Un CSS global du bundle (toPrint.css) impose « * { font-weight: normal
                !important } », ce qui écrase TOUT le gras (strong/b et fontWeight inline),
                à l'écran comme à l'impression. On le neutralise ICI, scopé au mandat, avec
                une spécificité supérieure à « * » (donc sans !important-war global). */}
            <style>
                {`
                    .mandat-mecanique strong,
                    .mandat-mecanique b,
                    .mandat-mecanique .mem-titre { font-weight: bold !important; }
                    @media print {
                        .mandat-mecanique { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                    }
                `}
            </style>

            {/* En-tête avec logos — masquable pour impression sur papier à en-tête. */}
            {afficherEnteteEtPied && (
            <div style={{ marginBottom: "30px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                    {sinistre?.contrat?.organisme?.typeOrganisme !== "POOL" && logoPreviewUrl && (
                        <img src={logoPreviewUrl} style={{ width: "auto", height: "60px", objectFit: "contain" }} alt="Logo compagnie" />
                    )}
                    <img src={entete} style={{ width: "auto", height: "60px", objectFit: "contain" }} alt="En-tête" />
                </div>
                <div style={{ height: "2px", background: "linear-gradient(to right, #2563eb, #3b82f6)", marginTop: "8px" }} />
            </div>
            )}

            {/* Papier à en-tête : on DESCEND le corps et on le CENTRE verticalement dans la
                feuille. Cet espace flexible en HAUT, combiné à celui du bas, centre le bloc
                (titre + corps + signature) au milieu de la page — le haut de la feuille étant
                occupé par l'en-tête pré-imprimé. Sans effet quand l'en-tête logiciel est affiché. */}
            {!afficherEnteteEtPied && <div style={{ flex: 1 }} />}

            {/* Titre */}
            <div style={{ textAlign: "center", marginBottom: "55px", marginTop: "40px" }}>
                <p className="mem-titre" style={{ margin: 0, fontSize: "14pt", fontWeight: "bold", textDecoration: "underline" }}>
                    MANDAT D'EXPERTISE MECANIQUE
                </p>
                <p className="mem-titre" style={{ margin: "10px 0 0 0", fontSize: "12pt", fontWeight: "bold", textDecoration: "underline" }}>
                    SINISTRE N° {numSinistre}{dateSinistre ? ` DU ${dateSinistre}` : ''}
                </p>
            </div>

            {/* Corps */}
            <div style={{ textAlign: "justify", fontSize: "12pt", lineHeight: 1.9 }}>
                <p style={{ marginBottom: "30px" }}>
                    Nous soussignés <strong>{societe}</strong>, mandatons par la présente le Cabinet{' '}
                    <strong>{nomExpert || '……………'}</strong>, à l'effet de procéder à l'expertise
                    mécanique du véhicule immatriculé <strong>{immatriculation}</strong> endommagé au cours
                    d'un accident survenu le <strong>{dateSinistre || '……………'}</strong> à {lieu}.
                </p>
                <p style={{ marginBottom: "30px" }}>
                    En foi de quoi, nous lui délivrons le présent mandat d'expertise pour
                    servir et valoir ce que de droit.
                </p>
            </div>

            {/* Signature */}
            <div style={{ marginTop: "90px", display: "flex", justifyContent: "flex-end" }}>
                <div style={{ textAlign: "left" }}>
                    <p style={{ margin: 0, fontSize: "12pt", textDecoration: "underline" }}>
                        Fait à Lomé, le {today}
                    </p>
                    <p style={{ margin: "4px 0 0 0", fontSize: "12pt", fontWeight: "bold" }}>
                        Pour la société
                    </p>
                </div>
            </div>

            {/* Espace flexible : pousse le pied de page en bas de la feuille A4. */}
            <div style={{ flex: 1 }} />

            {afficherEnteteEtPied && <DocumentFooter />}
        </div>
    )
})

MandatExpertiseMecanique.propTypes = {
    expertise: PropTypes.object,
    sinistre: PropTypes.object,
    /** false → mandat imprimé sans en-tête ni pied (papier à en-tête de l'utilisateur). */
    afficherEnteteEtPied: PropTypes.bool,
}

export default MandatExpertiseMecanique
